import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Database, FileStack, HelpCircle, RefreshCw, RotateCcw, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import XmlPendingDetailsList from "@/components/confronta/XmlPendingDetailsList";
import SurgicalQueueStatusBanner from "@/components/SurgicalQueueStatusBanner";
import { useSatUiDiagnose } from "@/lib/useSatUiDiagnose";
import {
  SAT_SYNC_SUPPORT_NOTICE,
  hasSatMonitorSyncProblem,
  useSatMonitorTechnicalAccess,
} from "@/lib/satMonitorTechnicalAccess";

const STATUS_LABEL = {
  SUCCESS: "Éxito",
  PROCESSING: "En curso",
  FAILED: "Error",
};

function TrafficLight({ status }) {
  const s = (status || "").toUpperCase();
  const color =
    s === "SUCCESS"
      ? "bg-emerald-500"
      : s === "PROCESSING"
        ? "bg-amber-400"
        : "bg-red-500";
  const title = STATUS_LABEL[s] || status || "—";
  return (
    <span className="inline-flex items-center gap-2" title={title}>
      <span
        className={`inline-block h-3 w-3 rounded-full shrink-0 ${color} ring-2 ring-offset-1 ring-black/10`}
        aria-hidden
      />
      <span className="text-sm text-slate-700">{title}</span>
    </span>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function formatCount(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("es-MX");
}

const SAT_STATUS_DISPLAY = {
  healthy: { emoji: "🟢", label: "Saludable" },
  warning: { emoji: "🟡", label: "Retraso Moderado" },
  critical: { emoji: "🔴", label: "Requiere Revisión" },
  no_evidence: { emoji: "⚪", label: "Sin evidencia SAT" },
};

function satStatusLine(status) {
  const s = SAT_STATUS_DISPLAY[status] || SAT_STATUS_DISPLAY.critical;
  return `${s.emoji} ${s.label}`;
}

/**
 * Panel de monitoreo: sincronización automática SAT (Fase 2) + resumen de CFDI almacenados.
 */
export default function SatSyncDashboard({
  clientId: filterClientId = null,
  showTechnicalActions: showTechnicalActionsProp,
}) {
  const { enabled: diagnoseEnabled } = useSatUiDiagnose();
  const technicalFromHook = useSatMonitorTechnicalAccess();
  const showTechnicalActions = showTechnicalActionsProp ?? technicalFromHook;
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [accessHint, setAccessHint] = useState(null);
  const [syncHealth, setSyncHealth] = useState(null);
  const [diagnoseOpen, setDiagnoseOpen] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setAccessHint(null);
    const params = { limit: 80 };
    if (filterClientId) params.client_id = filterClientId;
    try {
      const healthParams = filterClientId ? { client_id: filterClientId } : {};
      const [logsRes, summaryRes, healthRes] = await Promise.all([
        api.get("/sat/admin/sync/logs", { params }),
        api.get("/sat/admin/storage-summary", {
          params: filterClientId ? { client_id: filterClientId } : {},
        }),
        api.get("/sat/admin/sync-health", { params: healthParams }).catch(() => ({ data: null })),
      ]);
      setItems(logsRes.data?.items || []);
      setSummary(summaryRes.data || null);
      setSyncHealth(healthRes.data || null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setAccessHint(
          "No tiene permisos para ver la bitácora. Inicie sesión como titular o administrador del despacho.",
        );
      } else {
        toast.error(formatApiError(err, "No se pudo cargar el monitor de sincronización"));
      }
      setItems([]);
      setSummary(null);
      setSyncHealth(null);
    } finally {
      setLoading(false);
    }
  }, [filterClientId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleRetry = async (row) => {
    const cid = row.client_id;
    if (!cid) return;
    setRetryingId(row.id);
    try {
      const fd = new FormData();
      fd.append("client_id", cid);
      if (row.fecha_inicio) fd.append("fecha_inicio", String(row.fecha_inicio).slice(0, 10));
      if (row.fecha_fin) fd.append("fecha_fin", String(row.fecha_fin).slice(0, 10));
      const { data } = await api.post("/sat/admin/sync/retry", fd);
      toast.success(data?.message || "Reintento encolado");
      setTimeout(loadDashboard, 2500);
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo encolar el reintento"));
    } finally {
      setRetryingId(null);
    }
  };

  const handleReconcileXml = async () => {
    if (!filterClientId) {
      toast.info("Seleccione un cliente arriba para recalcular la integridad XML de ese contribuyente.");
      return;
    }
    setReconcileBusy(true);
    try {
      const { data } = await api.post("/sat/cfdi/reconcile-xml", {
        client_id: filterClientId,
        trigger_healing: false,
      });
      const linked = Number(data.reconciled) || 0;
      if (linked > 0) {
        toast.success(`${linked} comprobante(s) vinculados desde la base de datos.`);
      }
      await loadDashboard();
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo recalcular integridad XML"));
    } finally {
      setReconcileBusy(false);
    }
  };

  const scopeNote = filterClientId
    ? "Resumen del cliente seleccionado."
    : summary?.scope === "global"
      ? "Resumen de todos los contribuyentes visibles en esta instalación."
      : "Resumen de su espacio de trabajo (todos los clientes del despacho).";

  const showSupportNotice =
    !showTechnicalActions
    && !accessHint
    && hasSatMonitorSyncProblem(syncHealth, items);

  const logColSpan = showTechnicalActions ? 8 : 7;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">
          Herramienta principal de operación: aquí ves si el daemon SAT trabajó, falló o sigue en curso —
          sin adivinar si el sistema se colgó. El worker descarga en segundo plano a clientes con
          «Sincronización automática» activa en <strong>Clientes</strong>; la descarga manual FIEL no cambia.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDiagnoseOpen(true)}
            disabled={!syncHealth || !!accessHint}
          >
            <HelpCircle className="h-4 w-4 mr-1" />
            ¿Por qué no veo mis XML recientes?
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={loadDashboard} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {syncHealth && !accessHint ? (
        <div
          className="rounded-md border border-slate-200 bg-white p-4 space-y-3"
          data-testid="sat-sync-health-panel"
        >
          <p className="text-sm font-semibold text-[#0F2D5C] m-0">
            Estado SAT: {satStatusLine(syncHealth.status)}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">
                Última sincronización
              </p>
              <p className="m-0 mt-1 font-medium">{formatTs(syncHealth.last_sync)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">
                Próxima sincronización estimada
              </p>
              <p className="m-0 mt-1 font-medium">{formatTs(syncHealth.estimated_next_sync)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">
                Nivel de confianza
              </p>
              <p className="m-0 mt-1 font-medium">{syncHealth.confidence_label || syncHealth.confidence || "—"}</p>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 m-0 leading-snug">
            La hora estimada puede variar dependiendo de la disponibilidad de los servicios del SAT y de la cola de
            sincronización.
          </p>
          {syncHealth.message ? (
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 m-0">
              {syncHealth.message}
            </p>
          ) : null}
        </div>
      ) : null}

      <Dialog open={diagnoseOpen} onOpenChange={setDiagnoseOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>¿Por qué no veo mis XML recientes?</DialogTitle>
          </DialogHeader>
          {syncHealth ? (
            <ul className="text-sm space-y-2 text-slate-700 list-none p-0 m-0">
              <li>
                <strong>Última sincronización:</strong> {formatTs(syncHealth.last_sync)}
              </li>
              <li>
                <strong>Último intento:</strong> {formatTs(syncHealth.last_attempt)}
              </li>
              <li>
                <strong>Último éxito:</strong> {formatTs(syncHealth.last_success)}
              </li>
              <li>
                <strong>Próxima sincronización estimada:</strong>{" "}
                {formatTs(syncHealth.estimated_next_sync)}
              </li>
              <li>
                <strong>Estado actual:</strong>{" "}
                {syncHealth.current_status === "processing" ? "Sincronización en curso" : "En espera"}
                {" · "}
                {satStatusLine(syncHealth.status)}
              </li>
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No hay datos de sincronización disponibles.</p>
          )}
        </DialogContent>
      </Dialog>

      {accessHint ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {accessHint}
        </p>
      ) : null}

      {showSupportNotice ? (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
          data-testid="sat-sync-support-notice"
          role="status"
        >
          <p className="m-0 font-semibold text-amber-900">Solicitar revisión</p>
          <p className="m-0 mt-1.5 text-xs leading-relaxed">{SAT_SYNC_SUPPORT_NOTICE}</p>
        </div>
      ) : null}

      {diagnoseEnabled && summary?.surgical_queue && !accessHint ? (
        <SurgicalQueueStatusBanner surgicalQueue={summary.surgical_queue} />
      ) : null}

      {summary && !accessHint ? (
        <div
          className="rounded-md border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 space-y-3"
          data-testid="sat-sync-storage-summary"
        >
          <div className="flex items-start gap-2">
            <Database className="h-5 w-5 text-[#0F2D5C] shrink-0 mt-0.5" aria-hidden />
            <div>
              <p className="text-sm font-semibold text-[#0F2D5C] m-0">
                Almacenamiento de CFDI (copia local)
              </p>
              <p className="text-[11px] text-slate-500 m-0 mt-0.5">{scopeNote}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="rounded-sm border border-slate-200 bg-white px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">
                Total CFDI almacenados
              </p>
              <p
                className="text-2xl font-bold tabular-nums text-slate-900 m-0 mt-1"
                data-testid="sat-summary-total-cfdis"
              >
                {formatCount(summary.total_cfdis)}
              </p>
            </div>
            <div
              className={`rounded-sm border px-3 py-2.5 ${
                summary.integration_complete
                  ? "border-emerald-200 bg-emerald-50/80"
                  : summary.xml_pending_integration > 0
                    ? "border-amber-200 bg-amber-50/80"
                    : "border-slate-200 bg-white"
              }`}
              data-testid="sat-summary-xml-integrated"
            >
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">
                XML integrados
              </p>
              <p className="text-2xl font-bold tabular-nums text-slate-900 m-0 mt-1">
                {formatCount(summary.xml_integrated_estimate)}
              </p>
            </div>
            <div
              className={`rounded-sm border px-3 py-2.5 ${
                summary.xml_pending_integration > 0
                  ? "border-amber-300 bg-amber-50"
                  : "border-emerald-200 bg-emerald-50/80"
              }`}
              data-testid="sat-summary-xml-pending"
            >
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">
                XML pendientes
              </p>
              <p
                className={`text-2xl font-bold tabular-nums m-0 mt-1 ${
                  summary.xml_pending_integration > 0 ? "text-amber-900" : "text-emerald-800"
                }`}
              >
                {formatCount(summary.xml_pending_integration)}
              </p>
            </div>
            <div className="rounded-sm border border-slate-200 bg-white px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">
                Clientes con CFDI
              </p>
              <p className="text-lg font-semibold tabular-nums text-slate-900 m-0 mt-1">
                {formatCount(summary.clients_with_cfdi)}
                <span className="text-sm font-normal text-slate-500">
                  {" "}
                  / {formatCount(summary.clients_registered)} registrados
                </span>
              </p>
            </div>
            <div className="rounded-sm border border-slate-200 bg-white px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">
                Sincronización automática activa
              </p>
              <p className="text-lg font-semibold tabular-nums text-slate-900 m-0 mt-1">
                {formatCount(summary.clients_auto_sync_enabled)}
                <span className="text-sm font-normal text-slate-500"> clientes</span>
              </p>
            </div>
            <div className="rounded-sm border border-slate-200 bg-white px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">
                Última integración XML
              </p>
              <p className="text-sm font-medium text-slate-800 m-0 mt-1 whitespace-nowrap">
                {formatTs(summary.last_xml_ingested_at)}
              </p>
            </div>
          </div>

          {summary.total_cfdis > 0 ? (
            <div
              className={`rounded-sm border px-3 py-2.5 flex items-start gap-2 ${
                summary.integration_complete
                  ? "border-emerald-200 bg-emerald-50 text-emerald-950"
                  : "border-amber-200 bg-amber-50 text-amber-950"
              }`}
              data-testid="sat-summary-xml-integration-status"
            >
              {summary.integration_complete ? (
                <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" aria-hidden />
              ) : (
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" aria-hidden />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold m-0">
                  {summary.integration_complete
                    ? "Integridad XML al día"
                    : `${formatCount(summary.xml_pending_integration)} comprobante(s) sin archivo XML integrado`}
                </p>
                <p className="text-xs m-0 mt-1 leading-relaxed">
                  {summary.integration_complete
                    ? "Todos los registros almacenados tienen XML verificado en la copia local."
                    : "Hay metadatos en MongoDB pero falta el archivo XML en algunos comprobantes (carga manual omitida, descarga SAT incompleta o flag desactualizado)."}
                </p>
                {summary.pending_details?.length > 0 ? (
                  <div className="mt-3">
                    <p className="text-[11px] font-semibold m-0 mb-1.5">
                      Detalle ({summary.pending_details.length}
                      {summary.xml_pending_integration > summary.pending_details.length
                        ? ` de ${formatCount(summary.xml_pending_integration)}`
                        : ""}
                      )
                    </p>
                    <XmlPendingDetailsList items={summary.pending_details} showClient={!filterClientId} />
                  </div>
                ) : summary.xml_pending_integration > 0 && !filterClientId ? (
                  <p className="text-xs m-0 mt-2">
                    Seleccione un cliente en esta pantalla para ver UUID y fecha de cada pendiente.
                  </p>
                ) : null}
                {filterClientId && summary.xml_pending_integration > 0 && showTechnicalActions ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-3 h-8 rounded-sm"
                    disabled={reconcileBusy || loading}
                    onClick={handleReconcileXml}
                    data-testid="sat-monitor-reconcile-xml"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${reconcileBusy ? "animate-spin" : ""}`} />
                    Recalcular XML en BD
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          <p className="text-[11px] text-slate-500 m-0 leading-snug">
            {summary.disclaimer}
            {typeof summary.sync_logs_count === "number" ? (
              <>
                {" "}
                Registros en bitácora de sync automática:{" "}
                <strong className="tabular-nums">{formatCount(summary.sync_logs_count)}</strong>.
              </>
            ) : null}
          </p>
          {summary.total_cfdis > 0 ? (
            <div className="pt-1">
              <Button type="button" size="sm" className="rounded-sm" asChild data-testid="goto-mis-cfdi">
                <Link to="/contabilidad">
                  <FileStack className="h-4 w-4 mr-1.5" aria-hidden />
                  Ver documentos en Mis CFDI (tabla paginada)
                </Link>
              </Button>
              <p className="text-[10px] text-slate-500 m-0 mt-1.5">
                Esta pantalla muestra la bitácora del worker; los comprobantes están en la colección{" "}
                <strong>cfdis</strong>, no en sat_sync_logs.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Estado</TableHead>
              <TableHead>RFC</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Inicio</TableHead>
              <TableHead>Fin</TableHead>
              <TableHead className="text-right">CFDI</TableHead>
              <TableHead>Error</TableHead>
              {showTechnicalActions ? <TableHead className="w-[140px]" /> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={logColSpan} className="text-center text-slate-500 py-8">
                  <p className="m-0">Sin registros recientes de sincronización automática.</p>
                  {summary?.total_cfdis > 0 ? (
                    <p className="text-xs text-slate-600 mt-2 m-0">
                      Hay{" "}
                      <strong className="tabular-nums">{formatCount(summary.total_cfdis)}</strong> CFDI
                      en almacenamiento; el worker puede aún no haber generado bitácora para este periodo.
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400 mt-2 m-0">
                      Active la sincronización automática en Clientes o use la descarga manual con FIEL.
                    </p>
                  )}
                </TableCell>
              </TableRow>
            )}
            {items.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <TrafficLight status={row.status} />
                </TableCell>
                <TableCell className="font-mono text-xs">{row.rfc || "—"}</TableCell>
                <TableCell className="max-w-[180px] truncate" title={row.razon_social}>
                  {row.razon_social || "—"}
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">
                  {formatTs(row.timestamp_inicio)}
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">
                  {formatTs(row.timestamp_fin)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.cfdis_count ?? "—"}
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-xs text-red-700" title={row.error_message}>
                  {row.status === "FAILED" ? row.error_message || "—" : "—"}
                </TableCell>
                {showTechnicalActions ? (
                  <TableCell>
                    {(row.status === "FAILED" || row.status === "SUCCESS") && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={retryingId === row.id || !row.modulo_descarga_activo}
                        title={
                          row.modulo_descarga_activo
                            ? "Encolar nuevo job para el mismo periodo"
                            : "Active la sincronización automática en Clientes"
                        }
                        onClick={() => handleRetry(row)}
                        data-testid="sat-sync-retry-row"
                      >
                        <RotateCcw className={`h-4 w-4 mr-1 ${retryingId === row.id ? "animate-spin" : ""}`} />
                        Reintentar
                      </Button>
                    )}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
